import React, { useState } from 'react';
import { formatCountdown, formatTime, getTherapistName } from './utils';

function ConfirmStep({
  selectedCenter, selectedDate, selectedTime, guestInfo, guestCount,
  guestSelections, getTherapistPrice, getTotalForGuest,
  bookingNotes, onBookingNotesChange,
  reservationSeconds, onReservationExpired,
  policyAgreed, onPolicyChange, setStep,
  upkeepEligibility,
  couponCode, onCouponCodeChange, onApplyCoupon,
  invoiceTotals, upkeepApplied, reserving
}) {
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [couponExpanded, setCouponExpanded] = useState(false);
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const [policyExpanded, setPolicyExpanded] = useState(false);

  const originalTotal = guestSelections.reduce((sum, g) => sum + getTotalForGuest(g), 0);

  async function handleApplyCoupon() {
    if (!couponCode?.trim()) return;
    setCouponApplying(true);
    setCouponError('');
    setCouponSuccess('');
    const result = await onApplyCoupon(couponCode.trim());
    if (result.success) {
      setAppliedCouponCode(couponCode.trim().toUpperCase());
      setCouponSuccess('Coupon applied!');
      setTimeout(() => setCouponSuccess(''), 3000);
    } else {
      setCouponError(result.error || 'Invalid coupon code');
    }
    setCouponApplying(false);
  }

  return (
    <div className="step-content">
      {/* Reserving spinner */}
      {reserving && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
          <p style={{ color: '#888' }}>Reserving your time slot...</p>
        </div>
      )}

      {/* Reservation timer */}
      {!reserving && reservationSeconds !== null && reservationSeconds > 0 && (
        <div className={`bf-reservation-timer ${reservationSeconds <= 60 ? 'urgent' : ''}`}>
          Your reservation will expire in {formatCountdown(reservationSeconds)}
        </div>
      )}

      {/* Reservation expired modal */}
      {reservationSeconds === 0 && (
        <div className="bf-modal-overlay">
          <div className="bf-modal">
            <h3>YOUR RESERVATION HAS EXPIRED</h3>
            <p>Choose your date and time and reserve again.</p>
            <button className="auth-button" onClick={onReservationExpired}>OK</button>
          </div>
        </div>
      )}

      {!reserving && (
      <div className="bf-confirm-layout">
        {/* LEFT COLUMN — Booking details */}
        <div className="bf-confirm-left">
          <div className="bf-confirm-section">
            <div className="bf-confirm-field">
              <div className="bf-confirm-field-header">
                <strong>Date</strong>
                <button className="bf-edit-link" onClick={() => setStep(9)}>Edit</button>
              </div>
              {selectedDate && <p>{selectedDate.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>}
              {selectedTime && <p className="bf-confirm-detail">{formatTime(selectedTime)}</p>}
            </div>
          </div>

          <div className="bf-confirm-section">
            <div className="bf-confirm-field">
              <div className="bf-confirm-field-header">
                <strong>Location</strong>
                <button className="bf-edit-link" onClick={() => setStep(1)}>Edit</button>
              </div>
              <p>{selectedCenter?.name}</p>
              <p className="bf-confirm-detail">{selectedCenter?.address}</p>
            </div>
          </div>

          {guestInfo && (
            <div className="bf-confirm-section">
              <div className="bf-confirm-field">
                <strong>Guest{guestCount > 1 ? 's' : ''}</strong>
                <p>{guestInfo.firstName} {guestInfo.lastName}</p>
                {guestCount > 1 && guestSelections.slice(1).map((g, i) => (
                  <p key={i} className="bf-confirm-detail">{g.firstName || `Guest ${i + 2}`} {g.lastName || ''}</p>
                ))}
              </div>
            </div>
          )}

          <div className="bf-confirm-section">
            <div className="bf-confirm-field">
              <div className="bf-confirm-field-header">
                <strong>Services</strong>
                <button className="bf-edit-link" onClick={() => setStep(8.5)}>Edit</button>
              </div>
              {guestSelections.map((guest, idx) => guest.services && guest.services.length > 0 && (
                <div key={idx} className="bf-confirm-services">
                  {guestCount > 1 && <p className="bf-confirm-guest-label">{idx === 0 ? (guestInfo?.firstName || 'You') : (guest.firstName || `Guest ${idx + 1}`)}</p>}
                  {guest.services.map((item, i) => (
                    <div key={i} className="bf-confirm-service-item">
                      <p>{item.service.display_name || item.service.name}</p>
                      <p className="bf-confirm-detail">
                        with {(item.therapist?.Id || item.therapist?.id) === 'any' ? 'Any Provider' : getTherapistName(item.therapist)}
                        {item.service.duration > 0 ? ` · ${item.service.duration} mins` : ''}
                      </p>
                      {item.optionalAddOns?.map(a => (
                        <p key={a.id} className="bf-confirm-detail">+ {a.name}</p>
                      ))}
                      {item.mandatoryAddOns?.map(a => (
                        <p key={a.id} className="bf-confirm-detail">+ {a.name}</p>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="bf-confirm-section">
            <div className="bf-confirm-field">
              <label className="bf-confirm-notes-label">Notes or preferences</label>
              <textarea
                className="bf-confirm-textarea"
                placeholder="Anything we should know?"
                value={bookingNotes}
                onChange={(e) => onBookingNotesChange(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN — Total + Policy */}
        <div className="bf-confirm-right">
          <div className="bf-confirm-total-section">
            <div className="bf-confirm-total-row">
              <span>Total</span>
              <span className="bf-confirm-total-price">Payable at location</span>
            </div>
            <div className="bf-confirm-total-row bf-confirm-total-amount">
              <span></span>
              {invoiceTotals ? (
                <span>
                  {invoiceTotals.discount > 0 && (
                    <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.85em', marginRight: 8 }}>
                      ${originalTotal}
                    </span>
                  )}
                  ${invoiceTotals.total?.toFixed(2)}
                </span>
              ) : (
                <span>${originalTotal}</span>
              )}
            </div>

            {/* Per-service price breakdown */}
            <div className="bf-confirm-price-breakdown">
              {guestSelections.map((guest, idx) => guest.services && guest.services.length > 0 && (
                <React.Fragment key={idx}>
                  {guest.services.map((item, i) => (
                    <div key={i} className="bf-confirm-price-row">
                      <span>{item.service.display_name || item.service.name}</span>
                      <span>${getTherapistPrice(item.service, item.therapist).toFixed(2)}</span>
                    </div>
                  ))}
                  {guest.services.map((item, i) => (
                    <React.Fragment key={`addons-${i}`}>
                      {item.optionalAddOns?.map(a => (
                        <div key={a.id} className="bf-confirm-price-row sub">
                          <span>+ {a.name}</span>
                          <span>${a.price}</span>
                        </div>
                      ))}
                      {item.mandatoryAddOns?.map(a => (
                        <div key={a.id} className="bf-confirm-price-row sub">
                          <span>+ {a.name}</span>
                          <span>${a.price}</span>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
              {invoiceTotals && invoiceTotals.discount > 0 && (
                <div className="bf-confirm-price-row" style={{ color: '#2e9e5e', fontWeight: 600 }}>
                  <span>Discount</span>
                  <span>-${invoiceTotals.discount?.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Upkeep discount applied */}
            {upkeepApplied && (
              <div className="bf-upkeep-notice">
                10% upkeep discount applied
              </div>
            )}

            {/* Coupon code — collapsible */}
            <div className="bf-coupon-section">
              {appliedCouponCode ? (
                <p className="bf-coupon-applied">
                  Coupon <strong>{appliedCouponCode}</strong> applied
                  {invoiceTotals?.discount > 0 && <> — <span className="bf-coupon-applied-amount">-${invoiceTotals.discount.toFixed(2)}</span></>}
                </p>
              ) : couponExpanded ? (
                <>
                  <label className="bf-coupon-label">Have a coupon code?</label>
                  <div className="bf-coupon-row">
                    <input
                      type="text"
                      className="bf-coupon-input"
                      placeholder="Enter code"
                      value={couponCode}
                      autoFocus
                      onChange={(e) => { onCouponCodeChange(e.target.value.toUpperCase()); setCouponError(''); setCouponSuccess(''); }}
                    />
                    <button
                      className="bf-coupon-apply"
                      onClick={handleApplyCoupon}
                      disabled={!couponCode?.trim() || couponApplying}
                    >
                      {couponApplying ? '...' : 'APPLY'}
                    </button>
                  </div>
                  {couponError && <p className="bf-coupon-error">{couponError}</p>}
                  {couponSuccess && <p className="bf-coupon-success">{couponSuccess}</p>}
                </>
              ) : (
                <button
                  type="button"
                  className="bf-coupon-toggle"
                  onClick={() => setCouponExpanded(true)}
                >
                  Have a coupon? →
                </button>
              )}
            </div>
          </div>

          {/* Booking policies */}
          <div className="bf-confirm-policy">
            <h4>BOOKING POLICIES</h4>
            <ul className="bf-policy-list">
              <li>2-hour cancellation notice required</li>
              <li>No-shows charged 100%</li>
              <li>5+ min late may shorten or reschedule</li>
            </ul>
            <button
              type="button"
              className="bf-policy-toggle"
              onClick={() => setPolicyExpanded(p => !p)}
              aria-expanded={policyExpanded}
            >
              {policyExpanded ? 'Hide full policy ‹' : 'Full policy ›'}
            </button>
            {policyExpanded && (
              <div className="bf-policy-full">
                <p><strong>Cancellations:</strong> Please provide at least 2 hours' notice.</p>
                <p><strong>No-Shows:</strong> Missed appointments are charged 100%.</p>
                <p><strong>Late Arrivals:</strong> Arriving 5+ minutes late may shorten or require rescheduling.</p>
              </div>
            )}
            <label className="bf-policy-checkbox">
              <input type="checkbox" checked={policyAgreed} onChange={(e) => onPolicyChange(e.target.checked)} />
              <span>I agree to the booking policies.</span>
            </label>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default ConfirmStep;
